import { useEffect, useMemo, useState } from "react";

import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";
import useLocalization from "../../hooks/useLocalization";
import { exportToExcel } from "../../utils/exportToExcel";

import { Box, Grid, Tabs, Tab } from "@mui/material";

import EditorToolbar from "../editors/EditorToolbar";
import EntityList from "../pages/EntityList";
import Loading from "./Loading";
import Dialog_Flex from "./Dialog_Flex";
import Select from "../elements/Select";
import LocationsMap from "../elements/LocationsMap";
import MapActionMenu from "../elements/MapActionMenu";
import TerritoryPickerDialog from "../dialogs/TerritoryPickerDialog";

const TerritoryLocations = ({ syndicationId, projectId, territoryId }) => {
  const { windowHeight } = useBrowser();
  const { getListFromServer, getFromServer, executeAction, sortByFields } =
    useData();
  const { getTitle, getMessage } = useLocalization();

  const [territory, setTerritory] = useState(null);
  const [territories, setTerritories] = useState(null);
  const [locations, setLocations] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [openTerritoryDialog, setOpenTerritoryDialog] = useState(false);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState(null);
  const [openMapActionDialog, setOpenMapActionDialog] = useState(false);
  const [mapTerritories, setMapTerritories] = useState([]);
  const [loadingTerritories, setLoadingTerritories] = useState(false);
  const [assignedUser, setAssignedUser] = useState(null);
  const [retailerFilter, setRetailerFilter] = useState("all");
  const [bannerFilter, setBannerFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");

  const filteredLocations = useMemo(() => {
    const allLocations = Array.isArray(locations) ? locations : [];

    return allLocations.filter((locationRecord) => {
      if (
        retailerFilter !== "all" &&
        (locationRecord?.retailer || "") !== retailerFilter
      ) {
        return false;
      }

      if (
        bannerFilter !== "all" &&
        (locationRecord?.banner || "") !== bannerFilter
      ) {
        return false;
      }

      if (cityFilter !== "all" && (locationRecord?.city || "") !== cityFilter) {
        return false;
      }

      return true;
    });
  }, [locations, retailerFilter, bannerFilter, cityFilter]);

  const filterItems = useMemo(() => {
    const allLocations = Array.isArray(locations) ? locations : [];

    const uniqueRetailers = [
      ...new Set(
        allLocations
          .map((locationRecord) => locationRecord?.retailer)
          .filter((value) => typeof value === "string" && value.trim() !== ""),
      ),
    ]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: name, name }));

    const uniqueBanners = [
      ...new Set(
        allLocations
          .map((locationRecord) => locationRecord?.banner)
          .filter((value) => typeof value === "string" && value.trim() !== ""),
      ),
    ]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: name, name }));

    const uniqueCities = [
      ...new Set(
        allLocations
          .map((locationRecord) => locationRecord?.city)
          .filter((value) => typeof value === "string" && value.trim() !== ""),
      ),
    ]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: name, name }));

    return {
      retailers: uniqueRetailers,
      banners: uniqueBanners,
      cities: uniqueCities,
    };
  }, [locations]);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    if ((!projectId && !syndicationId) || !territoryId) return;

    const territoryData = await getFromServer({
      entityName: "Territory",
      id: territoryId,
    });
    setTerritory(territoryData);

    // Fetch assigned user with address coordinates if userId exists
    if (territoryData?.userId) {
      try {
        const userData = await getFromServer({
          entityName: "User",
          id: territoryData.userId,
        });
        if (userData?.address?.latitude && userData?.address?.longitude) {
          setAssignedUser(userData);
        }
      } catch (err) {
        console.error("Failed to fetch assigned user:", err);
      }
    }

    const locationData = await getListFromServer("TerritoryLocation", {
      territoryId,
    });
    if (locationData) {
      setLocations(locationData);
    } else {
      setLocations([]);
    }

    let filter = {};
    if (projectId) {
      filter = { projectId };
    } else {
      filter = { syndicationId };
    }

    const territoryList = await getFromServer({
      entityName: "Territory",
      filter: filter,
    });
    if (territoryList) {
      setTerritories(sortByFields(territoryList, ["name"]));
    } else {
      setTerritories([]);
    }
  };

  const handleSelectAll = (checked) => {
    if (!filteredLocations) return;

    setSelectedItems((current) => {
      let newItems = [...current];

      let deleteIndexes = [];

      for (let x = 0; x < filteredLocations.length; x++) {
        const index = newItems.findIndex(
          (id) => id === filteredLocations[x].id,
        );

        if (checked) {
          if (index === -1) {
            newItems.push(filteredLocations[x].id);
          }
        } else {
          if (index > -1) {
            deleteIndexes.push(index);
          }
        }
      }

      if (deleteIndexes.length > 0) {
        deleteIndexes.sort((a, b) => b - a);

        for (const delectedIndex of deleteIndexes) {
          newItems.splice(delectedIndex, 1);
        }
      }

      return newItems;
    });
  };

  const handleSelect = (id, checked) => {
    setSelectedItems((current) => {
      let newItems = [...current];

      if (checked) {
        if (!newItems.includes(id)) {
          newItems.push(id);
        }
      } else {
        const currentIndex = newItems.findIndex((item) => item === id);
        if (currentIndex > -1) {
          newItems.splice(currentIndex, 1);
        }
      }

      return newItems;
    });
  };

  const handleDelete = async () => {
    // console.log("DELETING.....");
  };

  const handleCustomOption = (name) => {
    switch (name) {
      case "move":
        setSelectedTerritoryId(null);
        setOpenTerritoryDialog(true);
        break;
      case "export":
        handleExport();
        break;
    }
  };

  const handleExport = () => {
    const rows = (
      Array.isArray(filteredLocations) ? filteredLocations : []
    ).map((locationRecord) => ({
      Territory:
        locationRecord?.territory || territory?.name || "Unassigned Territory",
      Location: locationRecord?.location || "",
      "Location #": locationRecord?.locationNumber || "",
      Retailer: locationRecord?.retailer || "",
      Banner: locationRecord?.banner || "",
      "Street Address": locationRecord?.streetAddress || "",
      City: locationRecord?.city || "",
      Province: locationRecord?.province || "",
      Postal: locationRecord?.postal || "",
      Latitude:
        locationRecord?.latitude !== undefined &&
        locationRecord?.latitude !== null
          ? locationRecord.latitude
          : "",
      Longitude:
        locationRecord?.longitude !== undefined &&
        locationRecord?.longitude !== null
          ? locationRecord.longitude
          : "",
    }));

    exportToExcel(rows, `territory_locations_${territory?.name || "export"}`);
  };

  const handleMapActionChangeTerritory = async () => {
    // Fetch territories with counts
    setLoadingTerritories(true);
    try {
      const response = await executeAction({
        entityName: "Territory",
        action: "getTerritoryOptionsWithCounts",
        data: {
          projectId: projectId || null,
          syndicationId: syndicationId || null,
        },
      });
      if (response?.isSuccessful && Array.isArray(response.data)) {
        setMapTerritories(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch territories", err);
    } finally {
      setLoadingTerritories(false);
    }
    setOpenMapActionDialog(true);
  };

  const handleMapActionConfirm = async (newTerritoryId) => {
    // Use bulkChangeTerritory action with context
    await executeAction({
      entityName: "TerritoryLocation",
      action: "bulkChangeTerritory",
      data: {
        locationIds: selectedItems,
        newTerritoryId: newTerritoryId,
        projectId: projectId || null,
        syndicationId: syndicationId || null,
      },
      displayMessage: true,
    });
    setOpenMapActionDialog(false);
    setSelectedItems([]);
    await getData();
  };

  const handleDialogAction = async (action) => {
    switch (action) {
      case "cancel":
        setOpenTerritoryDialog(false);
        setSelectedTerritoryId(null);
        break;

      case "change":
        await handleMove();
        break;
    }
  };

  const handleMove = async () => {
    await executeAction({
      entityName: "TerritoryLocation",
      action: "changeTerritory",
      data: {
        ids: selectedItems,
        territoryId: selectedTerritoryId,
      },
      displayMessage: true,
    });
    setSelectedTerritoryId(null);
    setOpenTerritoryDialog(false);
    await getData();
  };

  return (
    <>
      <Box>
        <EditorToolbar
          onDelete={handleDelete}
          customOptions={[
            { name: "move", iconName: "DriveFileMove" },
            { name: "export", iconName: "FileDownload" },
          ]}
          onCustomOption={handleCustomOption}
        />

        {/* View Mode Tabs */}
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          <Tab
            label="List View"
            value="list"
          />
          <Tab
            label="Map View"
            value="map"
          />
        </Tabs>

        <Box padding="20px">
          {!locations ? (
            <Loading />
          ) : viewMode === "list" ? (
            <Box>
              <Grid
                container
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{ minWidth: "200px" }}
                >
                  <Select
                    label="Retailer"
                    value={retailerFilter}
                    onChange={setRetailerFilter}
                    items={filterItems.retailers}
                    includeNone
                    noneLabel="All"
                  />
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{ minWidth: "200px" }}
                >
                  <Select
                    label="Banner"
                    value={bannerFilter}
                    onChange={setBannerFilter}
                    items={filterItems.banners}
                    includeNone
                    noneLabel="All"
                  />
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{ minWidth: "200px" }}
                >
                  <Select
                    label="City"
                    value={cityFilter}
                    onChange={setCityFilter}
                    items={filterItems.cities}
                    includeNone
                    noneLabel="All"
                  />
                </Grid>
              </Grid>

              <EntityList
                entityList={filteredLocations}
                allowSelect
                onSelect={handleSelect}
                onSelectAll={handleSelectAll}
                selectedItems={selectedItems}
                columns={[
                  { name: "location", colIndex: 1 },
                  { name: "locationNumber", colIndex: 2 },
                  { name: "streetAddress", colIndex: 3 },
                  { name: "city", colIndex: 3 },
                  { name: "province", colIndex: 4 },
                  { name: "postal", colIndex: 5 },
                ]}
                tableHeight={`${windowHeight - 250}px`}
              />
            </Box>
          ) : (
            <Box
              sx={{ position: "relative", height: `${windowHeight - 250}px` }}
            >
              <MapActionMenu
                mode="location"
                selectedCount={selectedItems.length}
                onChangeTerritory={handleMapActionChangeTerritory}
                onClearSelection={() => setSelectedItems([])}
              />
              <LocationsMap
                locations={locations.map((loc) => ({
                  id: loc.id,
                  name: loc.location,
                  number: loc.locationNumber,
                  address:
                    `${loc.streetAddress || ""} ${loc.city || ""}, ${loc.province || ""}`.trim(),
                  latitude: loc.latitude,
                  longitude: loc.longitude,
                }))}
                users={
                  assignedUser
                    ? [
                        {
                          id: assignedUser.id,
                          name: assignedUser.name,
                          latitude: assignedUser.address.latitude,
                          longitude: assignedUser.address.longitude,
                          address: [
                            assignedUser.address.streetAddress,
                            assignedUser.address.city,
                            assignedUser.address.province,
                          ]
                            .filter(Boolean)
                            .join(", "),
                        },
                      ]
                    : []
                }
                mode="location"
                selectedIds={new Set(selectedItems)}
                onToggleSelection={(id) => {
                  const locationRecord = locations.find((loc) => loc.id === id);
                  if (locationRecord) {
                    handleSelect(
                      locationRecord.id,
                      !selectedItems.includes(locationRecord.id),
                    );
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Territory change dialog for map view */}
      {openMapActionDialog && (
        <TerritoryPickerDialog
          isOpen={openMapActionDialog}
          territories={mapTerritories}
          isLoading={loadingTerritories}
          onSelect={handleMapActionConfirm}
          onClose={() => setOpenMapActionDialog(false)}
        />
      )}

      {/* Territory change dialog for list view */}
      {openTerritoryDialog ? (
        <Dialog_Flex
          title={getTitle("territory_change")}
          message={getMessage("territory_change")}
          open={openTerritoryDialog}
          onClose={() => setOpenTerritoryDialog(false)}
          actions={[
            { name: "cancel", label: "cancel" },
            { name: "change", label: "change" },
          ]}
          onAction={handleDialogAction}
        >
          <Box padding="20px">
            <Select
              label="territory"
              items={territories}
              value={selectedTerritoryId}
              onChange={(newValue) => setSelectedTerritoryId(newValue)}
            />
          </Box>
        </Dialog_Flex>
      ) : null}
    </>
  );
};

export default TerritoryLocations;
